import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAllPlans, type ServicePlan } from "@/lib/queries";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ChevronLeft, ExternalLink, AlertTriangle } from "lucide-react";
import {
  CATEGORIES,
  type CategoryValue,
  BILLING_CYCLES,
  type BillingCycle,
} from "@/lib/categories";
import { moneyInputProps, sanitizeMoney, parseMoneyInput } from "@/lib/money-input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({
    meta: [
      { title: "Admin — VISIKT" },
      { name: "description", content: "Catalog and inflation administration." },
      { name: "robots", content: "noindex" },
    ],
  }),
  beforeLoad: async ({ context }) => {
    const userId = (context as { user?: { id: string } }).user?.id;
    if (!userId) throw redirect({ to: "/auth" });
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .maybeSingle();
    if (!data) throw redirect({ to: "/dashboard", replace: true });
  },
  component: AdminPage,
});

type Tab = "overview" | "catalog" | "inflation" | "requests";

function AdminPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("overview");

  return (
    <div className="min-h-screen bg-background text-foreground pb-16">
      <header className="sticky top-0 z-20 bg-background/85 backdrop-blur-md border-b border-border">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to="/dashboard"
              className="size-8 grid place-items-center text-muted-foreground hover:text-foreground"
              aria-label={t("admin.backToApp")}
            >
              <ChevronLeft className="size-4" />
            </Link>
            <div className="min-w-0">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {t("admin.eyebrow")}
              </p>
              <h1 className="text-lg font-display font-bold tracking-tight">{t("admin.title")}</h1>
            </div>
          </div>
          <nav className="flex items-center text-[10px] font-mono uppercase tracking-widest border border-border rounded-full overflow-hidden">
            {(["overview", "catalog", "inflation", "requests"] as Tab[]).map((tk) => (
              <button
                key={tk}
                onClick={() => setTab(tk)}
                className={
                  "px-3 py-1.5 transition-colors " +
                  (tab === tk ? "bg-foreground text-background" : "text-muted-foreground")
                }
              >
                {tk === "overview"
                  ? "Overview"
                  : tk === "catalog"
                    ? t("admin.tabs.catalog")
                    : tk === "inflation"
                      ? t("admin.tabs.inflation")
                      : "Requests"}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 pt-8">
        {tab === "overview" && <OverviewAdmin />}
        {tab === "catalog" && <CatalogAdmin />}
        {tab === "inflation" && <InflationAdmin />}
        {tab === "requests" && <RequestsAdmin />}
      </main>
    </div>
  );
}

/* ============ Overview ============ */

function OverviewAdmin() {
  const usersQ = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, created_at")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 1000 * 60,
  });

  const subsQ = useQuery({
    queryKey: ["admin-subscriptions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscriptions")
        .select("id, user_id, is_active, monthly_cost_nok_equiv, created_at");
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 1000 * 60,
  });

  const salaryQ = useQuery({
    queryKey: ["admin-salary"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("salary_entries")
        .select("id, user_id, created_at");
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 1000 * 60,
  });

  const requestsQ = useQuery({
    queryKey: ["admin-requests-count"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("access_requests")
        .select("id, status, created_at")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 1000 * 30,
  });

  const users = usersQ.data ?? [];
  const subs = subsQ.data ?? [];
  const salaries = salaryQ.data ?? [];
  const requests = requestsQ.data ?? [];

  const now = new Date();
  const last30 = new Date(now.getTime() - 30 * 24 * 3600 * 1000);
  const last7 = new Date(now.getTime() - 7 * 24 * 3600 * 1000);

  const newUsersLast30 = users.filter((u) => new Date(u.created_at) > last30).length;
  const newUsersLast7 = users.filter((u) => new Date(u.created_at) > last7).length;
  const activeSubs = subs.filter((s) => s.is_active);
  const usersWithSubs = new Set(activeSubs.map((s) => s.user_id)).size;
  const usersWithSalary = new Set(salaries.map((s) => s.user_id)).size;
  const pendingRequests = requests.filter((r) => r.status === "pending").length;
  const avgSubsPerUser =
    users.length > 0 ? (activeSubs.length / Math.max(usersWithSubs, 1)).toFixed(1) : "—";

  const signupsByDay = users
    .filter((u) => new Date(u.created_at) > last30)
    .reduce<Record<string, number>>((acc, u) => {
      const day = u.created_at.slice(0, 10);
      acc[day] = (acc[day] ?? 0) + 1;
      return acc;
    }, {});

  const isLoading = usersQ.isLoading || subsQ.isLoading || salaryQ.isLoading || requestsQ.isLoading;

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  return (
    <div className="space-y-10">
      <section>
        <p className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-4">
          Platform
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MetricCard label="Total users" value={String(users.length)} />
          <MetricCard label="New (7d)" value={String(newUsersLast7)} />
          <MetricCard label="New (30d)" value={String(newUsersLast30)} />
          <MetricCard label="Active subs" value={String(activeSubs.length)} />
        </div>
      </section>

      <section>
        <p className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-4">
          Engagement
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MetricCard label="Users tracking subs" value={String(usersWithSubs)} />
          <MetricCard label="Users tracking salary" value={String(usersWithSalary)} />
          <MetricCard label="Avg subs / user" value={avgSubsPerUser} />
          <MetricCard
            label="Pending requests"
            value={String(pendingRequests)}
            tone={pendingRequests > 0 ? "warn" : undefined}
          />
        </div>
      </section>

      <section>
        <p className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-4">
          Sign-ups — last 30 days
        </p>
        {Object.keys(signupsByDay).length === 0 ? (
          <p className="text-sm text-muted-foreground">No sign-ups in this period.</p>
        ) : (
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border">
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-right">Sign-ups</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(signupsByDay)
                  .sort(([a], [b]) => b.localeCompare(a))
                  .map(([day, count]) => (
                    <tr key={day} className="border-b border-border/60 last:border-b-0">
                      <td className="px-3 py-2 font-mono tabular-nums">{day}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">{count}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <p className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-4">
          Recent sign-ups
        </p>
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border">
                <th className="px-3 py-2 text-left">User ID</th>
                <th className="px-3 py-2 text-right">Joined</th>
              </tr>
            </thead>
            <tbody>
              {users.slice(0, 10).map((u) => (
                <tr key={u.id} className="border-b border-border/60 last:border-b-0">
                  <td className="px-3 py-2 font-mono text-muted-foreground truncate max-w-[180px]">
                    {u.id}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-muted-foreground">
                    {new Date(u.created_at).toISOString().slice(0, 10)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function MetricCard({ label, value, tone }: { label: string; value: string; tone?: "warn" }) {
  return (
    <div className="border border-border rounded-lg p-4">
      <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-1">
        {label}
      </div>
      <div
        className={
          "text-2xl font-display font-bold tabular-nums " +
          (tone === "warn" ? "text-[color:var(--color-warning,#b45309)]" : "")
        }
      >
        {value}
      </div>
    </div>
  );
}

/* ============ Requests ============ */

function RequestsAdmin() {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["admin-requests"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("access_requests")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 1000 * 30,
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase.from("access_requests").update({ status }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-requests"] });
      qc.invalidateQueries({ queryKey: ["admin-requests-count"] });
      toast.success("Status updated");
    },
    onError: () => toast.error("Failed to update status"),
  });

  const requests = query.data ?? [];
  const pending = requests.filter((r) => r.status === "pending");
  const others = requests.filter((r) => r.status !== "pending");

  if (query.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-3 gap-3">
        <MetricCard label="Total requests" value={String(requests.length)} />
        <MetricCard
          label="Pending"
          value={String(pending.length)}
          tone={pending.length > 0 ? "warn" : undefined}
        />
        <MetricCard
          label="Invited"
          value={String(requests.filter((r) => r.status === "invited").length)}
        />
      </div>

      {requests.length === 0 ? (
        <p className="text-sm text-muted-foreground">No requests yet.</p>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border">
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">Heard about</th>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {[...pending, ...others].map((r) => (
                <tr key={r.id} className="border-b border-border/60 last:border-b-0">
                  <td className="px-3 py-2 font-semibold">{r.name}</td>
                  <td className="px-3 py-2 text-muted-foreground">{r.email}</td>
                  <td className="px-3 py-2 text-muted-foreground max-w-[140px] truncate">
                    {r.heard_about}
                  </td>
                  <td className="px-3 py-2 font-mono tabular-nums text-muted-foreground">
                    {new Date(r.created_at).toISOString().slice(0, 10)}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={
                        "font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border " +
                        (r.status === "pending"
                          ? "border-amber-500 text-amber-600"
                          : r.status === "invited"
                            ? "border-green-500 text-green-600"
                            : "border-border text-muted-foreground")
                      }
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <select
                      className="bg-transparent text-[10px] font-mono uppercase tracking-widest outline-none cursor-pointer text-muted-foreground hover:text-foreground"
                      value={r.status}
                      onChange={(e) => updateStatus.mutate({ id: r.id, status: e.target.value })}
                    >
                      <option value="pending">Pending</option>
                      <option value="invited">Invited</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ============ Catalog ============ */

interface AdminService {
  id: string;
  name: string;
  country: string;
  is_active: boolean;
  source_url: string | null;
  category_key: CategoryValue;
}

function useAdminServices() {
  return useQuery({
    queryKey: ["admin-services"],
    queryFn: async (): Promise<AdminService[]> => {
      const { data, error } = await supabase
        .from("services")
        .select("id,name,country,is_active,source_url, service_categories!inner(key)")
        .order("country")
        .order("name");
      if (error) throw error;
      type Row = {
        id: string;
        name: string;
        country: string;
        is_active: boolean;
        source_url: string | null;
        service_categories: { key: CategoryValue };
      };
      return ((data ?? []) as unknown as Row[]).map((r) => ({
        id: r.id,
        name: r.name,
        country: r.country,
        is_active: r.is_active,
        source_url: r.source_url,
        category_key: r.service_categories.key,
      }));
    },
    staleTime: 1000 * 30,
  });
}

function CatalogAdmin() {
  const { t } = useTranslation();
  const services = useAdminServices();
  const plans = useAllPlans();
  const qc = useQueryClient();
  const [filterCountry, setFilterCountry] = useState<string>("ALL");
  const [filterCategory, setFilterCategory] = useState<CategoryValue | "ALL">("ALL");
  const [showFetchModal, setShowFetchModal] = useState(false);

  useEffect(() => {
    if (!showFetchModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowFetchModal(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showFetchModal]);

  const countries = useMemo(() => {
    const set = new Set<string>();
    for (const s of services.data ?? []) set.add(s.country);
    return Array.from(set).sort();
  }, [services.data]);

  const filteredServices = useMemo(() => {
    return (services.data ?? []).filter((s) => {
      if (filterCountry !== "ALL" && s.country !== filterCountry) return false;
      if (filterCategory !== "ALL" && s.category_key !== filterCategory) return false;
      return true;
    });
  }, [services.data, filterCountry, filterCategory]);

  const plansByService = useMemo(() => {
    const m = new Map<string, ServicePlan[]>();
    for (const p of plans.data ?? []) {
      const arr = m.get(p.service_id) ?? [];
      arr.push(p);
      m.set(p.service_id, arr);
    }
    return m;
  }, [plans.data]);

  const stale30d = Date.now() - 30 * 24 * 3600 * 1000;
  const needVerify = (plans.data ?? []).filter(
    (p) => !p.last_verified_at || new Date(p.last_verified_at).getTime() < stale30d,
  ).length;

  const updatePlan = useMutation({
    mutationFn: async (input: Partial<ServicePlan> & { id: string }) => {
      const { id, ...rest } = input;
      const { error } = await supabase.from("service_plans").update(rest).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["all-plans"] });
      qc.invalidateQueries({ queryKey: ["service-plans"] });
      toast.success(t("admin.saved"));
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : t("common.somethingWrong")),
  });

  const toggleService = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const { error } = await supabase.from("services").update({ is_active: active }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-services"] });
      qc.invalidateQueries({ queryKey: ["service-catalog"] });
      toast.success(t("admin.saved"));
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : t("common.somethingWrong")),
  });

  return (
    <div className="space-y-6">
      {/* Overview cards */}
      <section className="grid grid-cols-3 gap-3">
        <StatCard label={t("admin.services")} value={String((services.data ?? []).length)} />
        <StatCard label={t("admin.plans")} value={String((plans.data ?? []).length)} />
        <StatCard
          label={t("admin.needsVerify")}
          value={String(needVerify)}
          tone={needVerify > 0 ? "warn" : undefined}
        />
      </section>

      {/* Filters */}
      <section className="flex flex-wrap gap-2 items-center border-y border-border py-3">
        <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
          {t("admin.filters.country")}
        </label>
        <select
          className="input-base !w-auto !py-1.5 text-xs"
          value={filterCountry}
          onChange={(e) => setFilterCountry(e.target.value)}
        >
          <option value="ALL">—</option>
          {countries.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground ml-3">
          {t("admin.filters.category")}
        </label>
        <select
          className="input-base !w-auto !py-1.5 text-xs"
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value as CategoryValue | "ALL")}
        >
          <option value="ALL">—</option>
          {CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setShowFetchModal(true)}
          className="ml-auto text-[10px] font-mono uppercase tracking-widest border border-border rounded-full px-3 py-1.5 text-muted-foreground hover:text-foreground"
        >
          {t("admin.fetchApi")}
        </button>
      </section>

      {/* Services + plans */}
      {services.isLoading || plans.isLoading ? (
        <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
      ) : filteredServices.length === 0 ? (
        <p className="text-sm text-muted-foreground">No services match the current filter.</p>
      ) : (
        <div className="space-y-4">
          {filteredServices.map((svc) => {
            const svcPlans = (plansByService.get(svc.id) ?? []).sort(
              (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
            );
            return (
              <details key={svc.id} className="border border-border rounded-lg overflow-hidden">
                <summary className="cursor-pointer list-none px-4 py-3 flex items-center justify-between gap-3 hover:bg-foreground/[0.02]">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-sm font-semibold truncate">{svc.name}</span>
                    <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                      {svc.country} · {svc.category_key}
                    </span>
                    {!svc.is_active && (
                      <span className="text-[9px] font-mono uppercase border border-border rounded px-1.5 py-0.5">
                        inactive
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {svc.source_url && (
                      <a
                        href={svc.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-muted-foreground hover:text-foreground"
                        aria-label={t("admin.columns.source")}
                      >
                        <ExternalLink className="size-3.5" />
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        toggleService.mutate({ id: svc.id, active: !svc.is_active });
                      }}
                      className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground"
                    >
                      {svc.is_active ? "deactivate" : "activate"}
                    </button>
                  </div>
                </summary>

                <div className="border-t border-border overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-left">
                      <tr className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border">
                        <th className="px-3 py-2">{t("admin.columns.plan")}</th>
                        <th className="px-3 py-2 w-24">{t("admin.columns.price")}</th>
                        <th className="px-3 py-2 w-28">{t("admin.columns.cycle")}</th>
                        <th className="px-3 py-2 w-32">{t("admin.columns.verified")}</th>
                        <th className="px-3 py-2 w-16">{t("admin.columns.active")}</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {svcPlans.map((p) => (
                        <PlanRow
                          key={p.id}
                          plan={p}
                          onSave={(patch) => updatePlan.mutate({ id: p.id, ...patch })}
                          stale={stale30d}
                        />
                      ))}
                      {svcPlans.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-3 py-4 text-muted-foreground">
                            No plans yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </details>
            );
          })}
        </div>
      )}

      {showFetchModal && (
        <div
          className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setShowFetchModal(false)}
        >
          <div
            className="max-w-sm w-full bg-background border border-border rounded-2xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-display font-bold mb-2">{t("admin.fetchApi")}</h2>
            <p className="text-sm text-muted-foreground mb-4">{t("admin.fetchApiBody")}</p>
            <button
              onClick={() => setShowFetchModal(false)}
              className="w-full py-2.5 rounded-md bg-foreground text-background text-sm font-medium"
            >
              {t("common.close")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PlanRow({
  plan,
  onSave,
  stale,
}: {
  plan: ServicePlan;
  onSave: (patch: Partial<ServicePlan>) => void;
  stale: number;
}) {
  const [price, setPrice] = useState(String(plan.monthly_price_nok));
  const [name, setName] = useState(plan.name);
  const [cycle, setCycle] = useState<BillingCycle>(plan.billing_cycle);
  const [active, setActive] = useState(plan.is_active ?? true);
  const lastVerified = plan.last_verified_at ? new Date(plan.last_verified_at) : null;
  const isStale = !lastVerified || lastVerified.getTime() < stale;

  const dirty =
    price !== String(plan.monthly_price_nok) ||
    name !== plan.name ||
    cycle !== plan.billing_cycle ||
    active !== (plan.is_active ?? true);

  function save() {
    const num = parseMoneyInput(price) ?? NaN;
    if (!Number.isFinite(num) || num < 0) {
      toast.error("Invalid price");
      return;
    }
    onSave({
      name: name.trim(),
      monthly_price_nok: num,
      billing_cycle: cycle,
      is_active: active,
      last_verified_at: new Date().toISOString(),
    });
  }

  return (
    <tr className="border-b border-border/60 last:border-b-0">
      <td className="px-3 py-2">
        <input
          className="bg-transparent w-full outline-none focus:bg-foreground/[0.04] rounded px-1.5 py-1"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </td>
      <td className="px-3 py-2">
        <input
          className="bg-transparent w-full outline-none focus:bg-foreground/[0.04] rounded px-1.5 py-1 tabular-nums"
          {...moneyInputProps}
          value={price}
          onChange={(e) => setPrice(sanitizeMoney(e.target.value))}
        />
      </td>
      <td className="px-3 py-2">
        <select
          className="bg-transparent outline-none focus:bg-foreground/[0.04] rounded px-1.5 py-1"
          value={cycle}
          onChange={(e) => setCycle(e.target.value as BillingCycle)}
        >
          {BILLING_CYCLES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </td>
      <td className="px-3 py-2">
        {lastVerified ? (
          <span
            className={
              isStale
                ? "text-[color:var(--color-warning,#b45309)] inline-flex items-center gap-1"
                : "text-muted-foreground"
            }
          >
            {isStale && <AlertTriangle className="size-3" />}
            {lastVerified.toISOString().slice(0, 10)}
          </span>
        ) : (
          <span className="text-[color:var(--color-warning,#b45309)] inline-flex items-center gap-1">
            <AlertTriangle className="size-3" /> never
          </span>
        )}
      </td>
      <td className="px-3 py-2">
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
      </td>
      <td className="px-3 py-2 text-right">
        <button
          type="button"
          disabled={!dirty}
          onClick={save}
          className="text-[10px] font-mono uppercase tracking-widest rounded px-2 py-1 bg-foreground text-background disabled:opacity-30"
        >
          save
        </button>
      </td>
    </tr>
  );
}

function StatCard({ label, value, tone }: { label: string; value: string; tone?: "warn" }) {
  return (
    <div className="border border-border rounded-lg p-4">
      <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-1">
        {label}
      </div>
      <div
        className={
          "text-2xl font-display font-bold tabular-nums " +
          (tone === "warn" ? "text-[color:var(--color-warning,#b45309)]" : "")
        }
      >
        {value}
      </div>
    </div>
  );
}

/* ============ Inflation ============ */

interface InflationRow {
  year: number;
  rate_percent: number;
  source: string;
  rate_kind: "arsvekst" | "tolvmanedersendring";
  as_of_month: number | null;
}

function InflationAdmin() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["admin-inflation"],
    queryFn: async (): Promise<InflationRow[]> => {
      const { data, error } = await supabase
        .from("inflation_rates")
        .select("year, rate_percent, source, rate_kind, as_of_month")
        .order("year", { ascending: false });
      if (error) throw error;
      return (data ?? []) as InflationRow[];
    },
  });

  const upsert = useMutation({
    mutationFn: async (row: InflationRow) => {
      const { error } = await supabase.from("inflation_rates").upsert(row, { onConflict: "year" });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-inflation"] });
      qc.invalidateQueries({ queryKey: ["inflation"] });
      toast.success(t("admin.saved"));
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : t("common.somethingWrong")),
  });

  const del = useMutation({
    mutationFn: async (year: number) => {
      const { error } = await supabase.from("inflation_rates").delete().eq("year", year);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-inflation"] });
      qc.invalidateQueries({ queryKey: ["inflation"] });
      toast.success(t("admin.deleted"));
    },
  });

  const [newYear, setNewYear] = useState(String(new Date().getFullYear()));
  const [newRate, setNewRate] = useState("");
  const [newSource, setNewSource] = useState("SSB KPI årsvekst");
  const [newKind, setNewKind] = useState<"arsvekst" | "tolvmanedersendring">("arsvekst");
  const [newAsOfMonth, setNewAsOfMonth] = useState(String(new Date().getMonth() + 1));

  function addNew(e: React.FormEvent) {
    e.preventDefault();
    const year = Number(newYear);
    const rate = parseMoneyInput(newRate) ?? NaN;
    if (!Number.isInteger(year) || year < 1900 || year > 2100) {
      toast.error("Invalid year");
      return;
    }
    if (!Number.isFinite(rate)) {
      toast.error("Invalid rate");
      return;
    }
    const asOfMonth = newKind === "tolvmanedersendring" ? Number(newAsOfMonth) : null;
    if (
      newKind === "tolvmanedersendring" &&
      (!Number.isInteger(asOfMonth) || asOfMonth! < 1 || asOfMonth! > 12)
    ) {
      toast.error(t("admin.inflation.invalidMonth"));
      return;
    }
    upsert.mutate({
      year,
      rate_percent: rate,
      source: newSource.trim() || "SSB KPI",
      rate_kind: newKind,
      as_of_month: asOfMonth,
    });
    setNewRate("");
  }

  return (
    <div className="space-y-6">
      <h2 className="font-display font-bold text-base">{t("admin.inflation.title")}</h2>
      <p className="text-xs text-muted-foreground max-w-prose">{t("admin.inflation.kindHelp")}</p>

      <form
        onSubmit={addNew}
        className="grid grid-cols-2 sm:grid-cols-3 gap-2 items-end border-y border-border py-4"
      >
        <label className="block">
          <span className="block font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">
            {t("admin.inflation.year")}
          </span>
          <input
            className="input-base"
            type="number"
            min={1900}
            max={2100}
            value={newYear}
            onChange={(e) => setNewYear(e.target.value)}
            required
          />
        </label>
        <label className="block">
          <span className="block font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">
            {t("admin.inflation.kind")}
          </span>
          <select
            className="input-base"
            value={newKind}
            onChange={(e) => setNewKind(e.target.value as "arsvekst" | "tolvmanedersendring")}
          >
            <option value="arsvekst">{t("admin.inflation.kindArsvekst")}</option>
            <option value="tolvmanedersendring">{t("admin.inflation.kindTolv")}</option>
          </select>
        </label>
        {newKind === "tolvmanedersendring" && (
          <label className="block">
            <span className="block font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">
              {t("admin.inflation.asOfMonth")}
            </span>
            <input
              className="input-base"
              type="number"
              min={1}
              max={12}
              value={newAsOfMonth}
              onChange={(e) => setNewAsOfMonth(e.target.value)}
              required
            />
          </label>
        )}
        <label className="block">
          <span className="block font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">
            {t("admin.inflation.rate")}
          </span>
          <input
            className="input-base tabular-nums"
            {...moneyInputProps}
            value={newRate}
            onChange={(e) => setNewRate(sanitizeMoney(e.target.value))}
            required
          />
        </label>
        <label className="block">
          <span className="block font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">
            {t("admin.inflation.source")}
          </span>
          <input
            className="input-base"
            value={newSource}
            onChange={(e) => setNewSource(e.target.value)}
            maxLength={120}
          />
        </label>
        <button
          type="submit"
          className="py-3 rounded-md bg-foreground text-background text-sm font-medium"
        >
          {t("admin.inflation.add")}
        </button>
      </form>

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
      ) : (query.data ?? []).length === 0 ? (
        <p className="text-sm text-muted-foreground">No data yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left">
            <tr className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border">
              <th className="px-3 py-2">{t("admin.inflation.year")}</th>
              <th className="px-3 py-2">{t("admin.inflation.kind")}</th>
              <th className="px-3 py-2">{t("admin.inflation.rate")}</th>
              <th className="px-3 py-2">{t("admin.inflation.source")}</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {(query.data ?? []).map((r) => (
              <InflationEditRow
                key={r.year}
                row={r}
                onSave={upsert.mutate}
                onDelete={() => del.mutate(r.year)}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function InflationEditRow({
  row,
  onSave,
  onDelete,
}: {
  row: InflationRow;
  onSave: (r: InflationRow) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const [rate, setRate] = useState(String(row.rate_percent));
  const [source, setSource] = useState(row.source);
  const [kind, setKind] = useState<"arsvekst" | "tolvmanedersendring">(row.rate_kind);
  const [asOfMonth, setAsOfMonth] = useState(String(row.as_of_month ?? new Date().getMonth() + 1));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const dirty =
    rate !== String(row.rate_percent) ||
    source !== row.source ||
    kind !== row.rate_kind ||
    (kind === "tolvmanedersendring" && asOfMonth !== String(row.as_of_month ?? ""));

  function handleSave() {
    const month = kind === "tolvmanedersendring" ? Number(asOfMonth) : null;
    if (kind === "tolvmanedersendring" && (!Number.isInteger(month) || month! < 1 || month! > 12)) {
      toast.error(t("admin.inflation.invalidMonth"));
      return;
    }
    onSave({
      year: row.year,
      rate_percent: parseMoneyInput(rate) ?? NaN,
      source: source.trim() || "SSB KPI",
      rate_kind: kind,
      as_of_month: month,
    });
  }

  return (
    <tr className="border-b border-border/60 last:border-b-0">
      <td className="px-3 py-2 font-mono tabular-nums">{row.year}</td>
      <td className="px-3 py-2">
        <div className="flex flex-col gap-1">
          <select
            className="bg-transparent text-[11px] outline-none focus:bg-foreground/[0.04] rounded px-1.5 py-1"
            value={kind}
            onChange={(e) => setKind(e.target.value as "arsvekst" | "tolvmanedersendring")}
          >
            <option value="arsvekst">{t("admin.inflation.kindArsvekst")}</option>
            <option value="tolvmanedersendring">{t("admin.inflation.kindTolv")}</option>
          </select>
          {kind === "tolvmanedersendring" && (
            <input
              className="bg-transparent w-20 text-[10px] outline-none focus:bg-foreground/[0.04] rounded px-1.5 py-1 tabular-nums"
              type="number"
              min={1}
              max={12}
              value={asOfMonth}
              onChange={(e) => setAsOfMonth(e.target.value)}
              placeholder={t("admin.inflation.asOfMonth")}
            />
          )}
        </div>
      </td>
      <td className="px-3 py-2">
        <input
          className="bg-transparent w-24 outline-none focus:bg-foreground/[0.04] rounded px-1.5 py-1 tabular-nums"
          {...moneyInputProps}
          value={rate}
          onChange={(e) => setRate(sanitizeMoney(e.target.value))}
        />
      </td>
      <td className="px-3 py-2">
        <input
          className="bg-transparent w-full outline-none focus:bg-foreground/[0.04] rounded px-1.5 py-1"
          value={source}
          onChange={(e) => setSource(e.target.value)}
        />
      </td>
      <td className="px-3 py-2 text-right space-x-2">
        <button
          type="button"
          disabled={!dirty}
          onClick={handleSave}
          className="text-[10px] font-mono uppercase tracking-widest rounded px-2 py-1 bg-foreground text-background disabled:opacity-30"
        >
          save
        </button>
        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          className="text-[10px] font-mono uppercase tracking-widest rounded px-2 py-1 border border-border text-[color:var(--color-negative)]"
        >
          del
        </button>
        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("admin.confirmDelete")}</AlertDialogTitle>
              <AlertDialogDescription>
                {row.year} — {row.rate_percent}%
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
              <AlertDialogAction onClick={() => onDelete()}>{t("common.delete")}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </td>
    </tr>
  );
}
